import React, { useEffect, useState } from 'react'

const INSIGHTS_PROC = { id: 110, name: 'kognative_INSIGHTS' }

const parseRows = (text) => {
  try {
    const data = JSON.parse(text)
    return Array.isArray(data) ? data : []
  } catch {
    return []
  }
}

const encodeSql = (sql) => {
  return btoa(unescape(encodeURIComponent(sql)))
}

const fetchTessBuddyCategoryId = async (orgData, listApiBase) => {
  const response = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('ReferenceData/ListCategories')}`, {
    headers: {
      'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
      'x-tessitura-url': listApiBase
    }
  })
  const text = await response.text()
  if (!response.ok) throw new Error(text.slice(0, 240) || `Tessitura returned ${response.status}.`)

  const categories = parseRows(text)
  const tessBuddy = categories.find(cat => String(cat.Description || cat.description || '').toLowerCase() === 'tess buddy')
  return tessBuddy?.Id || tessBuddy?.id || categories[0]?.Id || categories[0]?.id || 1
}

// ─── Donor Lifecycle stages ───────────────────────────────────────────────────

const DONOR_STAGES = [
  {
    key: 'prospect', icon: '🔍', label: 'Prospect', color: '#64748b', count: '—',
    description: 'Patrons with ticket activity in the last two years and no gift on record.',
    signal: 'Recent ticket purchase or attendance history with no contribution history.',
    action: 'Introduce the mission. Cultivation ask under $100. Include in annual fund broad outreach.',
    avgGift2026: '—',
    metrics: ['—', '—', '—'],
  },
  {
    key: 'firstgift', icon: '🌱', label: 'First Gift', color: '#16a34a', count: '—',
    description: 'Donors who have made exactly one gift in the current fiscal year, regardless of amount.',
    signal: 'Single current-year gift event. Watch for lapse risk within 90 days of first gift.',
    action: 'Send a personalized thank-you within 48 hrs. Invite to an insider event. Ask for a second gift within 6 months.',
    avgGift2026: '$126',
    metrics: ['—', '—', '—'],
  },
  {
    key: 'repeat', icon: '🔄', label: 'Repeat Donor', color: '#0369a1', count: '—',
    description: 'Donors with 2–4 gifts in the current fiscal year, cumulative current-year giving under $500.',
    signal: 'Sequential current-year giving pattern. Track year-over-year consistency.',
    action: 'Acknowledge giving streak. Offer a modest upgrade ask (+25%). Include in phonathon if lapse risk detected.',
    avgGift2026: '$82',
    metrics: ['—', '—', '—'],
  },
  {
    key: 'midlevel', icon: '⭐', label: 'Mid-Level', color: '#b45309', count: '—',
    description: 'Donors with $500–$4,999 in current fiscal year giving. Often overlooked — the bridge between annual fund and major gifts.',
    signal: 'Current-year giving momentum. Attendance at premium events. Multiple years on file.',
    action: 'Assign a relationship manager. Invite to backstage or artist events. Solicit for named or endowed fund.',
    avgGift2026: '$742',
    metrics: ['—', '—', '—'],
  },
  {
    key: 'major', icon: '🏆', label: 'Major Donor', color: '#7c3aed', count: '—',
    description: 'Donors with $5,000+ in current fiscal year giving or a current-year individual gift of $2,500+.',
    signal: 'Current-year capacity confirmed. Active in gala/special events. Multi-year history.',
    action: 'Personal face-to-face solicitation. Naming opportunity conversations. Board or advisory committee invitation.',
    avgGift2026: '$7,890',
    metrics: ['—', '—', '—'],
  },
  {
    key: 'legacy', icon: '🌟', label: 'Planned / Legacy', color: '#0891b2', count: '—',
    description: 'Donors who have indicated or are suspected of including the organization in their estate plans. Highest lifetime value segment.',
    signal: 'Age 65+, long giving history, expressed interest in legacy, engaged with planned giving collateral.',
    action: 'Introduce to planned giving staff. Recognize in legacy society. Keep engaged through intimate cultivation events.',
    avgGift2026: '$1,460',
    metrics: ['—', '—', '—'],
  },
]

// ─── Ticket Buyer Lifecycle stages (from marketing site) ──────────────────────

const TICKET_STAGES = [
  {
    key: 'single', icon: '🎟️', label: 'Single Ticket', color: '#16a34a', count: '—',
    status: 'Entry',
    description: 'Bought once. Still exploring. Prime target for a second visit offer.',
    signal: 'One transaction on record. Likely attended based on a recommendation or external promotion.',
    action: 'Second visit incentive (e.g. 2-for-1 offer). Welcome email series introducing the full season. Re-engage within 60 days before interest fades.',
    kpis: [
      { label: 'PPS', value: '$68' },
      { label: 'Ticket Revenue', value: '$418K' },
      { label: 'Seats', value: '6,147' },
    ],
    metrics: ['—', '—', '—'],
  },
  {
    key: 'multi', icon: '🎭', label: 'Multi Ticket', color: '#0369a1', count: '—',
    status: 'Engaged',
    description: 'Coming back. Showing preference. Ready to be introduced to a package.',
    signal: 'Two or more separate purchases. May show genre preference. Seasonal repeat likely.',
    action: 'Curated package offer based on past purchases. Highlight savings vs. single ticket pricing. Introduce subscription concept.',
    kpis: [
      { label: 'PPS', value: '$74' },
      { label: 'Ticket Revenue', value: '$712K' },
      { label: 'Seats', value: '9,621' },
    ],
    metrics: ['—', '—', '—'],
  },
  {
    key: 'package', icon: '📦', label: 'Package', color: '#b45309', count: '—',
    status: 'Committed',
    description: 'Committed to a run of shows. High conversion candidate for a full subscription.',
    signal: 'Bought a flex pack or mini-series. Selecting into a curated experience. Season thinking is forming.',
    action: 'Full-subscription pitch with exact $ savings. Highlight subscriber-only benefits. Personalized outreach from a patron services rep.',
    kpis: [
      { label: 'PPS', value: '$81' },
      { label: 'Ticket Revenue', value: '$486K' },
      { label: 'Seats', value: '6,000' },
    ],
    metrics: ['—', '—', '—'],
  },
  {
    key: 'subscription', icon: '❤️', label: 'Subscription', color: '#7c3aed', count: '—',
    status: 'Loyal',
    description: 'Your most loyal patrons. Highest lifetime value. The goal of the entire journey.',
    signal: 'Active subscription. Watch for early vs. late renewal behavior. Gift ask alongside renewal is highest-yield moment.',
    action: 'Early-bird renewal at week 8. Personal call for 5+ year subs at lapse risk. Upgrade seating offer. Ask for a gift alongside renewal.',
    kpis: [
      { label: 'PPS', value: '$92' },
      { label: 'Ticket Revenue', value: '$1.14M' },
      { label: 'Seats', value: '12,391' },
    ],
    metrics: ['—', '—', '—'],
  },
  {
    key: 'lapsed_ticket', icon: '🔁', label: 'Lapsed', color: '#dc2626', count: '—',
    status: 'Win-Back',
    description: 'Former ticket buyers with no purchase in 18+ months. High-history patrons are the most cost-effective to reactivate.',
    signal: 'Last order 18+ months ago. Drama, Opera genres common among high-value lapsed. Weeknight evening preference.',
    action: 'Win-back campaign with a strong first offer. Highlight new season programming. Personal call for former subscribers.',
    kpis: [
      { label: 'PPS', value: '$71' },
      { label: 'Ticket Revenue', value: '$284K' },
      { label: 'Seats', value: '4,000' },
    ],
    metrics: ['—', '—', '—'],
  },
]

// ─── Membership Status segments ───────────────────────────────────────────────

const MEMBERSHIP_SEGMENTS = [
  {
    key: 'current', icon: '✅', label: 'Current', color: '#16a34a', count: '—',
    description: 'Active members in good standing. Membership has not expired and they are receiving full benefits.',
    signal: 'Active membership record. Renewal date in the future. Full access to member benefits.',
    action: 'Stewardship touchpoints. Early renewal ask at 60 days before expiry. Upgrade or gift alongside renewal.',
    avgGift2026: '$214',
    metrics: ['—', '—', '—'],
    sql: `SELECT c.customer_no, c.sort_name, m.expiration_dt\nFROM t_customer c\nJOIN t_membership m ON m.customer_no = c.customer_no\nWHERE m.status = 'A'\n  AND m.expiration_dt >= GETDATE()\nORDER BY m.expiration_dt ASC`,
  },
  {
    key: 'grace', icon: '⏳', label: 'Grace Period', color: '#d97706', count: '—',
    description: 'Members whose membership has technically expired but are still within the grace period — still receiving benefits but overdue for renewal.',
    signal: 'Expiration date has passed. Grace period active. Still receiving member communications.',
    action: 'Urgent renewal reminder. Emphasize upcoming loss of benefits. Personal outreach for multi-year members.',
    avgGift2026: '$168',
    metrics: ['—', '—', '—'],
    sql: `SELECT c.customer_no, c.sort_name, m.expiration_dt,\n  DATEDIFF(day, m.expiration_dt, GETDATE()) AS days_past\nFROM t_customer c\nJOIN t_membership m ON m.customer_no = c.customer_no\nWHERE m.status = 'G'\nORDER BY m.expiration_dt ASC`,
  },
  {
    key: 'lapsed', icon: '❌', label: 'Lapsed', color: '#dc2626', count: '—',
    description: 'Former members who did not renew and are outside the grace window. Benefits have been revoked.',
    signal: 'No active membership. Grace period has expired. Last membership may be 1–5+ years ago.',
    action: 'Win-back campaign. Highlight membership changes and new benefits. First-year re-join offer at reduced rate.',
    avgGift2026: '$96',
    metrics: ['—', '—', '—'],
    sql: `SELECT c.customer_no, c.sort_name, MAX(m.expiration_dt) AS last_expiry\nFROM t_customer c\nJOIN t_membership m ON m.customer_no = c.customer_no\nWHERE m.status = 'L'\nGROUP BY c.customer_no, c.sort_name\nORDER BY last_expiry DESC`,
  },
]

// ─── Flow bar ─────────────────────────────────────────────────────────────────

function FlowBar({ stages }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '4px', marginBottom: '24px' }}>
      {stages.map((stage, i) => (
        <React.Fragment key={stage.key}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: stage.color, marginBottom: '4px' }} />
            <span style={{ fontSize: '10px', fontWeight: '700', color: stage.color, fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {stage.label}
            </span>
          </div>
          {i < stages.length - 1 && (
            <div style={{ flex: 1, height: '2px', background: `linear-gradient(to right, ${stage.color}60, ${stages[i + 1].color}60)`, minWidth: '20px', marginBottom: '18px' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// ─── Stage card (used for donor + ticket lifecycle) ───────────────────────────

function LoadingStatValue() {
  return <span style={s.loadingStatValue} aria-label="Loading lifecycle count" />
}

function StageCard({ stage, lifecycleType, onMakeList, listBuilding, listBuilt, message }) {
  const isDonorLifecycle = lifecycleType === 'donor'
  const isTicketLifecycle = lifecycleType === 'ticket'

  return (
    <div
      style={{
        background: 'white', borderRadius: '12px',
        border: `1px solid ${stage.color}30`,
        boxShadow: `0 2px 12px ${stage.color}12`,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>{stage.icon}</span>
            <div>
              {stage.status && (
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', fontFamily: "'Inter', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block' }}>
                  {stage.status}
                </span>
              )}
              <p style={{ fontSize: '14px', fontWeight: '700', color: stage.color, margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>{stage.label}</p>
            </div>
          </div>
          {stage.count && (
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '20px', fontWeight: '800', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>{stage.loading ? <LoadingStatValue /> : stage.count}</p>
              <p style={{ fontSize: '10px', color: '#9ca3af', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>constituents</p>
            </div>
          )}
        </div>
        <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.55', margin: '0 0 14px', fontFamily: "'Inter', sans-serif" }}>{stage.description}</p>

        <div style={{ marginBottom: '10px' }}>
          <p style={detailLabel}>Signal</p>
          <p style={detailText}>{stage.signal}</p>
        </div>
        {isDonorLifecycle && (
          <div style={s.kpiCard}>
            <p style={detailLabel}>Avg Gift (2026)</p>
            <p style={{ ...detailText, fontSize: '16px', fontWeight: '800', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" }}>{stage.avgGift2026}</p>
          </div>
        )}
        {isTicketLifecycle && (
          <div style={s.kpiGrid}>
            {stage.kpis.map(kpi => (
              <div key={kpi.label} style={s.kpiCard}>
                <p style={detailLabel}>{kpi.label}</p>
                <p style={{ ...detailText, fontSize: '15px', fontWeight: '800', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" }}>{kpi.value}</p>
              </div>
            ))}
          </div>
        )}
        {!isDonorLifecycle && !isTicketLifecycle && (
          <div style={{ marginBottom: '16px' }}>
            <p style={detailLabel}>Recommended Action</p>
            <p style={detailText}>{stage.action}</p>
          </div>
        )}

        <button
          onClick={() => onMakeList()}
          disabled={listBuilding || listBuilt}
          style={{
            padding: '7px 16px', borderRadius: '7px',
            background: listBuilt ? '#f0fff4' : listBuilding ? '#d1d9e6' : 'linear-gradient(135deg, #1d6fdb, #38bdf8)',
            color: listBuilt ? '#16a34a' : 'white',
            fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif",
            cursor: listBuilding || listBuilt ? 'default' : 'pointer',
            border: listBuilt ? '1px solid #c6f6d5' : 'none',
          }}
        >
          {listBuilt ? '✓ Segment created' : listBuilding ? 'Building…' : '+ Make a List'}
        </button>
        {message && (
          <p style={{ ...s.cardMessage, color: listBuilt ? '#16a34a' : '#dc2626' }}>{message}</p>
        )}
      </div>
    </div>
  )
}

const detailLabel = { fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', fontFamily: "'Inter', sans-serif" }
const detailText  = { fontSize: '12px', color: '#374151', lineHeight: '1.6', margin: 0, fontFamily: "'Inter', sans-serif" }

// ─── Lifecycle tab (donor or ticket) ─────────────────────────────────────────

function LifecycleTab({ orgData, stages, flowTitle, lifecycleType }) {
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [buildingList, setBuildingList] = useState(null)
  const [builtLists, setBuiltLists] = useState({})
  const [cardMessages, setCardMessages] = useState({})
  const tessituraAuth = orgData?.organizations?.tessitura_auth_string
  const tessituraUrl = orgData?.organizations?.tessitura_base_url
  const orgReady = !!(tessituraAuth && tessituraUrl)

  const procHeaders = {
    'x-tessitura-auth': tessituraAuth,
    'x-tessitura-url': tessituraUrl,
    'Content-Type': 'application/json'
  }

  const listApiBase = () => {
    return (tessituraUrl || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const callInsightsProc = async (parameterValues) => {
    const response = await fetch('/api/tessitura?endpoint=Custom/Execute', {
      method: 'POST',
      headers: procHeaders,
      body: JSON.stringify({
        ProcedureId: INSIGHTS_PROC.id,
        ProcedureName: INSIGHTS_PROC.name,
        ParameterValues: parameterValues
      })
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchStats = async () => {
    if (!orgReady) return

    setLoading(true)
    setMessage(null)
    try {
      const result = await callInsightsProc([
        { Name: '@mode', Value: lifecycleType === 'donor' ? 'DONOR_LIFECYCLE' : 'TICKET_LIFECYCLE' }
      ])

      if (!result.ok) {
        setMessage({ type: 'error', text: 'Could not load lifecycle counts.' })
        setLoading(false)
        return
      }

      setStats(parseRows(result.text))
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load lifecycle counts: ' + err.message })
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!orgReady) return
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifecycleType, orgReady, tessituraAuth, tessituraUrl])

  const getStatForStage = (key) => {
    return stats.find(row => String(row.stage_key || '').toLowerCase() === key)
  }

  const displayStages = stages.map(stage => ({
    ...stage,
    loading,
    count: getStatForStage(stage.key)?.customer_count?.toLocaleString?.() ?? getStatForStage(stage.key)?.customer_count ?? '—'
  }))

  const handleMakeList = async (stage) => {
    setCardMessages(prev => ({ ...prev, [stage.key]: null }))
    setBuildingList(stage.key)
    try {
      const sqlResult = await callInsightsProc([
        { Name: '@mode', Value: lifecycleType === 'donor' ? 'DONOR_LIFECYCLE_LIST_SQL' : 'TICKET_LIFECYCLE_LIST_SQL' },
        { Name: '@bucket_key', Value: stage.key }
      ])

      if (!sqlResult.ok) {
        setCardMessages(prev => ({ ...prev, [stage.key]: 'Could not build lifecycle list SQL.' }))
        setBuildingList(null)
        return
      }

      const sqlRow = parseRows(sqlResult.text)[0] || {}
      if (!sqlRow.list_sql) {
        setCardMessages(prev => ({ ...prev, [stage.key]: 'The lifecycle list SQL did not come back from Tessitura.' }))
        setBuildingList(null)
        return
      }

      const categoryId = await fetchTessBuddyCategoryId(orgData, listApiBase())
      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: (sqlRow.description || stage.label).slice(0, 30) },
            { Name: '@list_sql_b64', Value: encodeSql(sqlRow.list_sql) },
            { Name: '@category_id', Value: String(categoryId) }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        setCardMessages(prev => ({ ...prev, [stage.key]: createText.slice(0, 240) || `Tessitura returned ${createResp.status}.` }))
        setBuildingList(null)
        return
      }

      setBuiltLists(prev => ({ ...prev, [stage.key]: true }))
      setCardMessages(prev => ({ ...prev, [stage.key]: `${stage.label} lifecycle list created.` }))
    } catch (err) {
      setCardMessages(prev => ({ ...prev, [stage.key]: err.message }))
    }
    setBuildingList(null)
  }

  return (
    <div>
      <div style={s.flowCard}>
        <p style={s.flowTitle}>{flowTitle}</p>
        <FlowBar stages={displayStages} />
      </div>
      {message && (
        <div style={{ ...s.messageBox, color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {message.text}
        </div>
      )}
      <div style={s.grid}>
        {displayStages.map((stage) => (
          <StageCard
            key={stage.key}
            stage={stage}
            lifecycleType={lifecycleType}
            onMakeList={() => handleMakeList(stage)}
            listBuilding={buildingList === stage.key}
            listBuilt={!!builtLists[stage.key]}
            message={cardMessages[stage.key]}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Membership tab ───────────────────────────────────────────────────────────

function MembershipTab({ orgData }) {
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [buildingList, setBuildingList] = useState(null)
  const [builtLists, setBuiltLists] = useState({})
  const [cardMessages, setCardMessages] = useState({})
  const tessituraAuth = orgData?.organizations?.tessitura_auth_string
  const tessituraUrl = orgData?.organizations?.tessitura_base_url
  const orgReady = !!(tessituraAuth && tessituraUrl)

  const procHeaders = {
    'x-tessitura-auth': tessituraAuth,
    'x-tessitura-url': tessituraUrl,
    'Content-Type': 'application/json'
  }

  const listApiBase = () => {
    return (tessituraUrl || '')
      .replace(/\/TessituraService\/?$/i, '/Tessitura/api/')
  }

  const callInsightsProc = async (parameterValues) => {
    const response = await fetch('/api/tessitura?endpoint=Custom/Execute', {
      method: 'POST',
      headers: procHeaders,
      body: JSON.stringify({
        ProcedureId: INSIGHTS_PROC.id,
        ProcedureName: INSIGHTS_PROC.name,
        ParameterValues: parameterValues
      })
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchStats = async () => {
    if (!orgReady) return

    setLoading(true)
    setMessage(null)
    try {
      const result = await callInsightsProc([{ Name: '@mode', Value: 'MEMBERSHIP_LIFECYCLE' }])
      if (!result.ok) {
        setMessage({ type: 'error', text: 'Could not load membership counts.' })
        setLoading(false)
        return
      }
      setStats(parseRows(result.text))
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not load membership counts: ' + err.message })
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!orgReady) return
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgReady, tessituraAuth, tessituraUrl])

  const getStatForSegment = (key) => {
    return stats.find(row => String(row.stage_key || '').toLowerCase() === key)
  }

  const displaySegments = MEMBERSHIP_SEGMENTS.map(seg => ({
    ...seg,
    loading,
    count: getStatForSegment(seg.key)?.customer_count?.toLocaleString?.() ?? getStatForSegment(seg.key)?.customer_count ?? '—'
  }))

  const handleMakeList = async (seg) => {
    setCardMessages(prev => ({ ...prev, [seg.key]: null }))
    setBuildingList(seg.key)
    try {
      const sqlResult = await callInsightsProc([
        { Name: '@mode', Value: 'MEMBERSHIP_LIFECYCLE_LIST_SQL' },
        { Name: '@bucket_key', Value: seg.key }
      ])
      const sqlRow = parseRows(sqlResult.text)[0] || {}
      if (!sqlResult.ok || !sqlRow.list_sql) {
        setCardMessages(prev => ({ ...prev, [seg.key]: 'Could not build membership list SQL.' }))
        setBuildingList(null)
        return
      }

      const categoryId = await fetchTessBuddyCategoryId(orgData, listApiBase())
      const createResp = await fetch(`/api/tessitura?endpoint=${encodeURIComponent('Custom/Execute')}`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData?.organizations?.tessitura_auth_string,
          'x-tessitura-url': listApiBase(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureName: 'kognative_CREATE_TESSBUDDY_LIST',
          ParameterValues: [
            { Name: '@description', Value: (sqlRow.description || seg.label).slice(0, 30) },
            { Name: '@list_sql_b64', Value: encodeSql(sqlRow.list_sql) },
            { Name: '@category_id', Value: String(categoryId) }
          ]
        })
      })

      const createText = await createResp.text()
      if (!createResp.ok) {
        setCardMessages(prev => ({ ...prev, [seg.key]: createText.slice(0, 240) || `Tessitura returned ${createResp.status}.` }))
        setBuildingList(null)
        return
      }

      setBuiltLists(prev => ({ ...prev, [seg.key]: true }))
      setCardMessages(prev => ({ ...prev, [seg.key]: `${seg.label} membership list created.` }))
    } catch (err) {
      setCardMessages(prev => ({ ...prev, [seg.key]: err.message }))
    }
    setBuildingList(null)
  }

  return (
    <div>
      {/* Summary bar */}
      <div style={s.flowCard}>
        <p style={s.flowTitle}>Membership Status Overview</p>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {displaySegments.map(seg => (
            <div key={seg.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
              <span style={{ fontSize: '12px', fontWeight: '700', color: seg.color, fontFamily: "'Inter', sans-serif" }}>{seg.label}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontFamily: "'Inter', sans-serif" }}>{seg.loading ? '...' : seg.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Status cards — always expanded, no counts */}
      <div style={s.grid}>
        {displaySegments.map(seg => {
          const isBuilding = buildingList === seg.key
          const isBuilt    = !!builtLists[seg.key]

          return (
            <div
              key={seg.key}
              style={{
                background: 'white', borderRadius: '12px',
                border: `1px solid ${seg.color}30`,
                boxShadow: `0 2px 12px ${seg.color}12`,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '22px' }}>{seg.icon}</span>
                    <p style={{ fontSize: '15px', fontWeight: '700', color: seg.color, margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>{seg.label}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '22px', fontWeight: '800', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>{seg.loading ? <LoadingStatValue /> : seg.count}</p>
                    <p style={{ fontSize: '10px', color: '#9ca3af', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>members</p>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.55', margin: '0 0 14px', fontFamily: "'Inter', sans-serif" }}>{seg.description}</p>

                <div style={{ marginBottom: '10px' }}>
                  <p style={detailLabel}>Signal</p>
                  <p style={detailText}>{seg.signal}</p>
                </div>
                <div style={s.kpiCard}>
                  <p style={detailLabel}>Avg Gift (2026)</p>
                  <p style={{ ...detailText, fontSize: '16px', fontWeight: '800', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" }}>{seg.avgGift2026}</p>
                </div>
                <button
                  onClick={() => handleMakeList(seg)}
                  disabled={isBuilding || isBuilt}
                  style={{
                    padding: '7px 16px', borderRadius: '7px',
                    background: isBuilt ? '#f0fff4' : isBuilding ? '#d1d9e6' : 'linear-gradient(135deg, #1d6fdb, #38bdf8)',
                    color: isBuilt ? '#16a34a' : 'white',
                    fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif",
                    cursor: isBuilding || isBuilt ? 'default' : 'pointer',
                    border: isBuilt ? '1px solid #c6f6d5' : 'none',
                  }}
                >
                  {isBuilt ? '✓ Segment created' : isBuilding ? 'Building…' : '+ Add to Segment'}
                </button>
                {cardMessages[seg.key] && (
                  <p style={{ ...s.cardMessage, color: isBuilt ? '#16a34a' : '#dc2626' }}>{cardMessages[seg.key]}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {message && (
        <div style={{ ...s.messageBox, color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {message.text}
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

function Lifecycles({ embedded, orgData }) {
  const [activeTab, setActiveTab] = useState('donor')

  return (
    <div>
      {!embedded && (
        <div style={s.header}>
          <h1 style={s.title}>Lifecycles</h1>
          <p style={s.subtitle}>
            Understand where your constituents sit in their journey — and what actions to take at each stage.
          </p>
        </div>
      )}

      <div style={s.tabBar}>
        {[
          { key: 'donor',      label: '🎁 Donor Lifecycle' },
          { key: 'ticket',     label: '🎟️ Ticket Buyer Lifecycle' },
          { key: 'membership', label: '🪪 Membership Status' },
        ].map(t => (
          <button
            key={t.key}
            style={activeTab === t.key ? s.tabActive : s.tab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={activeTab === 'donor' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <LifecycleTab orgData={orgData} stages={DONOR_STAGES} flowTitle="Donor Journey" lifecycleType="donor" />
      </div>
      <div style={activeTab === 'ticket' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <LifecycleTab orgData={orgData} stages={TICKET_STAGES} flowTitle="Patron Journey" lifecycleType="ticket" />
      </div>
      <div style={activeTab === 'membership' ? s.visibleTabPanel : s.hiddenTabPanel}>
        <MembershipTab orgData={orgData} />
      </div>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  header:   { marginBottom: '24px' },
  title:    { fontSize: '24px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  subtitle: { fontSize: '13px', color: '#6b7280', fontFamily: "'Inter', sans-serif", lineHeight: '1.6', maxWidth: '580px' },

  tabBar:   { display: 'flex', gap: '4px', borderBottom: '1px solid rgba(29,111,219,0.15)', marginBottom: '24px' },
  visibleTabPanel: { display: 'block' },
  hiddenTabPanel: { display: 'none' },
  tab:      { padding: '10px 18px', backgroundColor: 'transparent', color: '#4b5563', border: 'none', borderBottom: '2px solid transparent', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  tabActive:{ padding: '10px 18px', backgroundColor: 'transparent', color: '#0c1a33', border: 'none', borderBottom: '2px solid #1d6fdb', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },

  flowCard:  { background: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', marginBottom: '20px', maxWidth: '960px' },
  flowTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '14px', fontFamily: "'Inter', sans-serif" },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', maxWidth: '960px' },
  kpiCard: { marginBottom: '16px', padding: '10px 12px', background: '#f8fafd', border: '1px solid rgba(29,111,219,0.08)', borderRadius: '8px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginBottom: '16px' },
  loadingStatValue: { display: 'inline-block', width: '48px', height: '20px', borderRadius: '7px', background: 'linear-gradient(135deg, rgba(29,111,219,0.12), rgba(56,189,248,0.28))', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  cardMessage: { margin: '8px 0 0', fontSize: '11px', fontWeight: '600', lineHeight: '1.35', fontFamily: "'Inter', sans-serif" },
  messageBox: { margin: '0 0 14px', padding: '10px 14px', backgroundColor: '#f8fafd', border: '1px solid rgba(29,111,219,0.12)', borderRadius: '8px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", maxWidth: '960px' },
}

export default Lifecycles
